import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import {
    getAllRegionSettings,
    createRegionSettings,
    updateRegionSettings,
    deleteRegionSettings,
    getRegionSettingsById,
    RegionSettingsParams,
    CreateRegionSettingsData
} from "../../../redux/Actions/RegionSettingsAction";
import { 
    clearSelectedRegionSettings, 
    clearError,
    setCurrentPage,
    setCurrentLimit
} from "../../../redux/Slices/RegionSettingsSlice";

export const useRegionSettings = () => {
    const dispatch = useAppDispatch();
    const { 
        regionSettings, 
        selectedRegionSettings, 
        loading, 
        error,
        totalCount,
        hasNextPage,
        currentPage,
        currentLimit
    } = useAppSelector((state) => state.regionSettings);

    // Get all region settings
    const fetchRegionSettings = useCallback(async (params: RegionSettingsParams) => {
        return await dispatch(getAllRegionSettings(params));
    }, [dispatch]);

    // Create new region settings
    const createNewRegionSettings = useCallback(async (data: CreateRegionSettingsData) => {
        return await dispatch(createRegionSettings({ data }));
    }, [dispatch]);

    // Update existing region settings
    const updateExistingRegionSettings = useCallback(async (id: string, data: CreateRegionSettingsData) => {
        return await dispatch(updateRegionSettings({ id, data }));
    }, [dispatch]);

    // Delete region settings
    const deleteExistingRegionSettings = useCallback(async (id: string) => {
        return await dispatch(deleteRegionSettings({ id }));
    }, [dispatch]);

    // Get region settings by ID
    const fetchRegionSettingsById = useCallback(async (id: string) => {
        return await dispatch(getRegionSettingsById({ id }));
    }, [dispatch]);

    // Clear selected region settings
    const clearSelected = useCallback(() => {
        dispatch(clearSelectedRegionSettings());
    }, [dispatch]);

    // Clear error
    const clearErrorState = useCallback(() => {
        dispatch(clearError());
    }, [dispatch]);

    // Set current page
    const setPage = useCallback((page: number) => {
        dispatch(setCurrentPage(page));
    }, [dispatch]);

    // Set current limit
    const setLimit = useCallback((limit: number) => {
        dispatch(setCurrentLimit(limit));
    }, [dispatch]);

    return {
        // State
        regionSettings,
        selectedRegionSettings,
        loading,
        error,
        totalCount,
        hasNextPage,
        currentPage,
        currentLimit,

        // Actions
        fetchRegionSettings,
        createNewRegionSettings,
        updateExistingRegionSettings,
        deleteExistingRegionSettings,
        fetchRegionSettingsById,
        clearSelected,
        clearErrorState,
        setPage,
        setLimit,
    };
};
